import axios from 'axios';
import { API_BASE_URL, handleApiError } from './_apiBase';
import { AlertRule, AlertContact, AlertHistoryEntry, DeviationReport } from '../types';
import { ApiMessage, MonitorTemplate } from '../types/api';

/** Det som skickas in när en regel skapas eller ändras: regelns fält plus kontakt-id:n. */
export type AlertRuleInput = Partial<Omit<AlertRule, 'id' | 'contacts' | 'devices'>> & {
    contact_ids?: number[];
    device_ieee_list?: string[];
};

/** Det som skickas in när en kontakt skapas eller ändras. */
export type AlertContactInput = Partial<Omit<AlertContact, 'id'>>;

// ── Alert Rules & Contacts ────────────────────────────────────────────────────

export const fetchAlertRules = async (): Promise<AlertRule[]> => {
    try {
        const res = await axios.get<AlertRule[]>(`${API_BASE_URL}/alert-rules`);
        return res.data;
    } catch (error) { return handleApiError(error, 'Hämta larmregler'); }
};

export const createAlertRule = async (rule: AlertRuleInput): Promise<ApiMessage> => {
    try {
        const res = await axios.post<ApiMessage>(`${API_BASE_URL}/alert-rules`, rule);
        return res.data;
    } catch (error) { return handleApiError(error, 'Skapa larmregel'); }
};

export const updateAlertRule = async (id: number, rule: AlertRuleInput): Promise<ApiMessage> => {
    try {
        const res = await axios.put<ApiMessage>(`${API_BASE_URL}/alert-rules/${id}`, rule);
        return res.data;
    } catch (error) { return handleApiError(error, 'Spara larmregel'); }
};

export const toggleAlertRule = async (id: number): Promise<ApiMessage> => {
    try {
        const res = await axios.patch<ApiMessage>(`${API_BASE_URL}/alert-rules/${id}/toggle`);
        return res.data;
    } catch (error) { return handleApiError(error, 'Slå på eller av larmregel'); }
};

/**
 * Servern svarar 409 med historyCount om regeln har larmhändelser som skulle försvinna.
 * Då returneras { historyCount } utan att något raderats; anroparen frågar användaren
 * och anropar igen med force = true.
 */
export type DeleteRuleResult = ApiMessage | { historyCount: number };

export const deleteAlertRule = async (id: number, force = false): Promise<DeleteRuleResult> => {
    try {
        const res = await axios.delete<ApiMessage>(`${API_BASE_URL}/alert-rules/${id}`, { params: force ? { force: 1 } : {} });
        return res.data;
    } catch (error: any) {
        if (error?.response?.status === 409 && error.response.data?.historyCount != null) {
            return { historyCount: Number(error.response.data.historyCount) };
        }
        return handleApiError(error, 'Ta bort larmregel');
    }
};

export const fetchRuleImage = async (id: number): Promise<string | null> => {
    try {
        const res = await axios.get<{ instruction_image?: string | null }>(`${API_BASE_URL}/alert-rules/${id}/image`);
        return res.data?.instruction_image ?? null;
    } catch { return null; }
};

export const fetchAlertContacts = async (): Promise<AlertContact[]> => {
    try {
        const res = await axios.get<AlertContact[]>(`${API_BASE_URL}/alert-rules/contacts`);
        return res.data;
    } catch (error) { return handleApiError(error, 'Hämta kontakter'); }
};

export const createAlertContact = async (contact: AlertContactInput): Promise<ApiMessage> => {
    try {
        const res = await axios.post<ApiMessage>(`${API_BASE_URL}/alert-rules/contacts`, contact);
        return res.data;
    } catch (error) { return handleApiError(error, 'Skapa kontakt'); }
};

export const updateAlertContact = async (id: number, contact: AlertContactInput): Promise<ApiMessage> => {
    try {
        const res = await axios.put<ApiMessage>(`${API_BASE_URL}/alert-rules/contacts/${id}`, contact);
        return res.data;
    } catch (error) { return handleApiError(error, 'Spara kontakt'); }
};

export const deleteAlertContact = async (id: number): Promise<ApiMessage> => {
    try {
        const res = await axios.delete<ApiMessage>(`${API_BASE_URL}/alert-rules/contacts/${id}`);
        return res.data;
    } catch (error) { return handleApiError(error, 'Ta bort kontakt'); }
};

/**
 * Set exactly which rules a contact receives, for both engines.
 *
 * An omitted list leaves that side untouched; an empty array clears it. Replaces the
 * old add-only call, which could not remove an assignment.
 */
export const setContactRules = async (
    contactId: number,
    ruleIds?: number[],
    advancedRuleIds?: number[],
): Promise<ApiMessage> => {
    try {
        const body: Record<string, number[]> = {};
        if (ruleIds) body.rule_ids = ruleIds;
        if (advancedRuleIds) body.advanced_rule_ids = advancedRuleIds;
        const res = await axios.put<ApiMessage>(`${API_BASE_URL}/alert-rules/contacts/${contactId}/rules`, body);
        return res.data;
    } catch (error) { return handleApiError(error, 'Koppla kontakt till regler'); }
};

export const fetchAlertHistory = async (limit = 100): Promise<AlertHistoryEntry[]> => {
    try {
        const res = await axios.get<AlertHistoryEntry[]>(`${API_BASE_URL}/alert-rules/history?limit=${limit}`);
        return res.data;
    } catch (error) { return handleApiError(error, 'Hämta larmhistorik'); }
};

export const fetchDeviations = async (): Promise<DeviationReport[]> => {
    try {
        const res = await axios.get<DeviationReport[]>(`${API_BASE_URL}/alert-rules/deviations`);
        return res.data;
    } catch (error) { return handleApiError(error, 'Hämta avvikelserapporter'); }
};

// ── Push Notifications ────────────────────────────────────────────────────────

export const fetchVapidPublicKey = async (): Promise<string> => {
    try {
        const res = await axios.get<{ publicKey?: string }>(`${API_BASE_URL}/alert-rules/push-vapid-key`);
        return res.data.publicKey || '';
    } catch { return ''; }
};

export const registerPushSubscription = async (contactId: number, sub: PushSubscription): Promise<void> => {
    const json = sub.toJSON();
    await axios.post(`${API_BASE_URL}/alert-rules/contacts/${contactId}/push-subscribe`, {
        endpoint: sub.endpoint,
        p256dh: json.keys?.p256dh,
        auth: json.keys?.auth,
    });
};

export const unregisterPushSubscription = async (contactId: number, endpoint: string): Promise<void> => {
    await axios.delete(`${API_BASE_URL}/alert-rules/contacts/${contactId}/push-subscribe`, {
        data: { endpoint },
    });
};

/** Den inloggades egen kontakt (samma e-post som användaren), eller null. */
export interface MyContact {
    id: number;
    name: string;
    notify_push: boolean;
    /** Webbläsare som är kopplade till kontakten. */
    push_endpoints: string[];
}

export const fetchMyContact = async (): Promise<MyContact | null> => {
    const res = await axios.get<{ contact: MyContact | null }>(`${API_BASE_URL}/alert-rules/my-contact`);
    return res.data.contact;
};

/** Kopplar webbläsaren till den egna kontakten och slår på push på den. */
export const registerMyPushSubscription = async (sub: PushSubscription): Promise<void> => {
    const json = sub.toJSON();
    await axios.post(`${API_BASE_URL}/alert-rules/my-contact/push-subscribe`, {
        endpoint: sub.endpoint,
        p256dh: json.keys?.p256dh,
        auth: json.keys?.auth,
    });
};

export const unregisterMyPushSubscription = async (endpoint: string): Promise<void> => {
    await axios.delete(`${API_BASE_URL}/alert-rules/my-contact/push-subscribe`, { data: { endpoint } });
};

export const submitDeviation = async (
    token: string,
    payload: { reporter_name: string | null; reason: string; action_taken: string }
): Promise<Partial<DeviationReport> & { error?: string }> => {
    try {
        const res = await axios.post<Partial<DeviationReport> & { error?: string }>(`${API_BASE_URL.replace('/api', '')}/api/deviation/${token}`, payload);
        return res.data;
    } catch (error) { return handleApiError(error, 'Skicka in avvikelserapport'); }
};

export const fetchDeviationSettings = async (): Promise<{ reminder_hours: number }> => {
    try {
        const res = await axios.get<{ reminder_hours: number }>(`${API_BASE_URL}/alert-rules/deviation-settings`);
        return res.data;
    } catch (error) { return handleApiError(error, 'Hämta inställningar för avvikelserapporter'); }
};

export const updateDeviationSettings = async (settings: { reminder_hours: number }): Promise<ApiMessage> => {
    try {
        const res = await axios.put<ApiMessage>(`${API_BASE_URL}/alert-rules/deviation-settings`, settings);
        return res.data;
    } catch (error) { return handleApiError(error, 'Spara inställningar för avvikelserapporter'); }
};

// ── Monitor Templates ────────────────────────────────────────────────────────

export const fetchMonitorTemplates = async (): Promise<MonitorTemplate[]> => {
    try {
        const res = await axios.get<MonitorTemplate[]>(`${API_BASE_URL}/monitor-templates`);
        return res.data;
    } catch (error) { return handleApiError(error, 'Hämta mallar'); }
};

export const createMonitorTemplate = async (template: Omit<MonitorTemplate, 'id'>): Promise<MonitorTemplate> => {
    try {
        const res = await axios.post<MonitorTemplate>(`${API_BASE_URL}/monitor-templates`, template);
        return res.data;
    } catch (error) { return handleApiError(error, 'Skapa mall'); }
};

export const deleteMonitorTemplate = async (id: number): Promise<ApiMessage> => {
    try {
        const res = await axios.delete<ApiMessage>(`${API_BASE_URL}/monitor-templates/${id}`);
        return res.data;
    } catch (error) { return handleApiError(error, 'Ta bort mall'); }
};

// ── Kontaktgrupper ────────────────────────────────────────────────────────────

/** En grupp av kontakter, t.ex. "Köket". Kan vara ansvarig för ett schema i egenkontrollen. */
export interface ContactGroup {
    id: number;
    name: string;
    members: { id: number; name: string; email: string | null }[];
    /** Hur många scheman som har gruppen som ansvarig. */
    schedule_count: number;
    /** Larmregler som har gruppen som mottagare, vanliga och avancerade. */
    rules: { id: number; name: string }[];
    advanced_rules: { id: number; name: string }[];
}

/** Sätter exakt vilka larm gruppen ska få. Utelämnad lista rörs inte, tom lista rensar. */
export const setContactGroupRules = async (id: number, ruleIds?: number[], advancedRuleIds?: number[]): Promise<ContactGroup> => {
    try {
        const body: Record<string, number[]> = {};
        if (ruleIds) body.rule_ids = ruleIds;
        if (advancedRuleIds) body.advanced_rule_ids = advancedRuleIds;
        return (await axios.put<ContactGroup>(`${API_BASE_URL}/contact-groups/${id}/rules`, body)).data;
    } catch (error) { return handleApiError(error, 'Koppla grupp till regler'); }
};

export const fetchContactGroups = async (): Promise<ContactGroup[]> => {
    try { return (await axios.get<ContactGroup[]>(`${API_BASE_URL}/contact-groups`)).data; }
    catch (error) { return handleApiError(error, 'Hämta grupper'); }
};

export const createContactGroup = async (body: { name: string; contact_ids: number[] }): Promise<ContactGroup> => {
    try { return (await axios.post<ContactGroup>(`${API_BASE_URL}/contact-groups`, body)).data; }
    catch (error) { return handleApiError(error, 'Skapa grupp'); }
};

export const updateContactGroup = async (id: number, body: { name: string; contact_ids: number[] }): Promise<ContactGroup> => {
    try { return (await axios.put<ContactGroup>(`${API_BASE_URL}/contact-groups/${id}`, body)).data; }
    catch (error) { return handleApiError(error, 'Spara grupp'); }
};

export const deleteContactGroup = async (id: number): Promise<void> => {
    try { await axios.delete(`${API_BASE_URL}/contact-groups/${id}`); }
    catch (error) { return handleApiError(error, 'Ta bort grupp'); }
};
